/**
 *  Module for create configure.
 */

const CreateConfigure = function (app) {

  app.performance.mark('configure:ui:create:start');

  const $ = require('jquery');
  const _ = require('lodash');
  const configure = app.configure;
  const $container = app.$container;
  const mustache = require('mustache');

  function showUnavailableSelections(unavailable) {
    let markup = require(__dirname + '/../../templates/unavailable/unavailable.html');

    markup = mustache.render(markup, {
      recipeId: app.mergedParams.recipeId,
      message: app.l10n.getLang('unavailable_message', 'It looks like some of the items in your custom design are no longer available. Please select another option.'),
      unavailable,
      unavailable_item_message: app.l10n.getLang('unavailable_item_message', 'Unfortunately, this item is out of stock.'),
      button_label: app.l10n.getLang('unavailable_button_label', 'Back'),
      image_size: (app.utils.isMobile() || app.utils.isVerticalTablet()) ? 220 : 360
    });

    return markup;
  }

  /**
   *  bypassKnownUnavailable is a helper function to remove some selections from
   *  the unavailable dialog. This is usually related to data attributes or
   *  values that do not impact the UI. Usually a None value or a Yes/No value.
   *  These change over time due to customer requests and we need to prevent
   *  them from displaying in the UI until the recipe's used in production can
   *  be updated
   *
   *  We have also added support to remove any OMS attribute from the dialog
   **/
  function bypassKnownUnavailable(unavailable) {
    /**
     * Matches is an array of CA/AV combinations that need to be removed from
     * the unavailable dialog.
     *
     * Currently the alias and AV ID are required values to function properly
     */
    const matches = [
      { ca: { alias: 'right_chest' }, av: { id: 95738 } },
      { ca: { alias: 'right_hem' }, av: { id: 95738 } }
    ];

    return _.compact(_.map(unavailable, (u) => {
      const match = _.find(matches, (m) => {
        return (
          m.ca.alias === u.ca.alias &&
          m.av.id === u.av.id
        );
      });

      // Check to see if the unavailable attribute is an OMS attribute
      const isOms = u.ca.alias.indexOf('oms_') >= 0 && u.ca.selectorType === 'none';

      // If we were unable to find a restricted selection then return the object
      if (!match && !isOms) {
        return u;
      }
    }));
  }

  // Takes the tokens {Attribute_value_123456} and finds the value by ID
  function processLanguageToken(alias) {
    const id = _.parseInt(app.recipeDocument.configuration[alias].match(/[0-9]/g).join(''));
    const value = _.find(configure.run('getAttribute', { alias }).values, { id });

    return value;
  }

  function checkForSize(unavailable) {
    return _.map(unavailable, (item) => {
      if (item.ca.alias === 'size') {
        let style = '';
        let fit = '';
        let color = '';

        if (app.recipeDocument) {
          style = processLanguageToken('style').name;
          fit = processLanguageToken('fit').name;
          color = processLanguageToken('color').name;
        }

        const size = item.av.name;

        item.av.name = `${color} ${style} ${fit} ${size}`;

        if (app.appUtils.isDefaultRecipe()) {
          app.analytics.track('replace-default-recipe', {
            recipeId: app.mergedParams.recipeId,
            productId: app.mergedParams.product
          });
        }
      }

      return item;
    });
  }

  function createConfigureUI(unavailable) {
    // Reset the configure container
    $container.html('');

    // Define the markup to be used
    const preloader = require(__dirname + '/../../templates/preloader/circular.html'),
      product = app.product,
      // If the description is empty set an empty object string as the value
      description = product.description || '{}',
      descriptionJson = $.parseJSON(description);
    let markup = '',
      brand = app.l10n.getLang('brand_name', 'Ralph Lauren'),
      timer;

    // Pull the correct markup template based on the users device
    if (app.utils.isMobile() || app.utils.isVerticalTablet()) {
      markup = require(__dirname + '/../../templates/configure/mobile/configure.html');
    } else {
      markup = require(__dirname + '/../../templates/configure/desktop/configure.html');
    }

    // RAL-2352 / Add support to let the UI know when the Configure DOM is ready
    if (typeof window.configureLoaded === 'function') {
      window.configureLoaded();
    }

    // If the product facet "Brand" is defined we want to use that value
    if (product.facets.hasOwnProperty('Brand')) {
      brand = product.facets.Brand[0];
    }

    // Is Customize it product check
    let customizeAttr;
    try {
      customizeAttr = configure.run('getAttribute', { alias: 'customize_selection' }); // change to facet check
    } catch (err) {
      customizeAttr = false;
    }
    // Set the customize_selection_mobile state to trigger visibility rules
    if (app.helpers.hasAttribute('customize_selection_mobile')) {
      configure.run('selectValue', {
        'ca': { 'alias': 'customize_selection_mobile' },
        'av': { 'vendorId': (app.utils.isMobile() || app.utils.isVerticalTablet()) ? 'Yes' : 'No' }
      });
    }

    // Parse out the template constiables with localized text
    const notes_label = app.l10n.getLang('notes_label', 'Important Notes');
    const standardShippingMessage = app.l10n.getLang('accordion_shipping_message', 'Ships in 3-4 business days. Delivery date is dependent upon shipping method.');
    const dtgShippingMessage = app.l10n.getLang('accordion_shipping_message_dtg', 'Ships in 5-7 business days. Delivery date is dependent upon shipping method.');
    const locale = app.mergedParams.locale;
    const options = {
      brand_name: brand,
      catalog_header: app.l10n.getLang('catalog_header', 'You Might Also Like'),
      details: descriptionJson.details,
      details_header: app.l10n.getLang('details_header', 'Additional Information'),
      details_label: app.l10n.getLang('details_label', 'Product Details'),
      isFluidEnv: app.isFluidEnv(),
      locale,
      notes: app.appUtils.typography(descriptionJson.style_notes),
      notes_label: descriptionJson.style_notes === '' ? '' : notes_label,
      price_total_label: app.l10n.getLang('price_total_label', 'Total'),
      product,
      random_aria_label: app.l10n.getLang('random_aria_label', 'Suprise Me'),
      start_over_aria_label: app.l10n.getLang('start_over_aria_label', 'Start Over'),
      style_number: product.vendorId,
      style_number_label: app.l10n.getLang('style_number_label', 'Store Style #:'),
      zoom_label: app.l10n.getLang('model_zoom_hover_label', 'Mouse Over to Zoom'),
      logo_img: app.mergedParams.assetPath + '/img/pride-logo.svg',
      logo_gif: app.mergedParams.assetPath + '/img/ringanimation.gif',
      review_img: app.mergedParams.assetPath + '/img/dev_review.jpg',
      product_category: app.category.toLowerCase().replace(/ /g, '-'),
      project_name: app.projectName.toLowerCase().replace(/ /g, '-'),
      customize_label: customizeAttr.name,
      hasCustomizeIt: !!customizeAttr,
      hasCartBar: app.projectName.toLowerCase() === 'pride',
      isAppInstance: app.isAppInstance()
    };
    const campaign = app.appUtils.isCampaign();

    options.disclaimer_copy = app.l10n.getLang('disclaimer_copy', 'Customized items are final sale and may not be returned or exchanged.');;
    options.accordion_shipping_message = app.appUtils.isDTGProduct() ? dtgShippingMessage : standardShippingMessage;

    if (campaign === 'Design Contest') {
      if (locale === 'en_us') {
        options.accordion_shipping_message = '';
      } else {
        options.disclaimer_copy = '';
      }
    }

    markup = mustache.render(markup, options);

    // Add the markup to the DOM
    $container.append(markup).addClass(() => {
      if (campaign) {
        return `fc-campaign-${_.kebabCase(campaign)}`;
      }
    });

    if (!app.mergedParams.configureLoaded) {
      $container.find('.fc-preloader-container').html(preloader);
    } else {
      $container.find('.fc-preloader-container').remove();
    }

    // If the product is a Tag then add a class to the main wrapper
    if (product.name.indexOf('Tag') >= 0) {
      $container.find('.fc-menu-container').addClass('fc-menu-product-swatches');
    }

    const uiSettings = configure.run('getUiSettings');
    if (typeof uiSettings.displayRotations !== 'undefined') {
      app.views = uiSettings.displayRotations.horizontal;
    } else {
      app.views = [uiSettings.displayThumbnails.views[0]];
    }
    if (app.appUtils.isDTGProduct()) {
      const hideTooltipsStyle = '<style>.fc-tooltip { display: none !important;}</style>';
      $('head').append(hideTooltipsStyle);
    }

    if (app.product.id === 22949) {
      app.views = ['on_model'].concat(app.views);
    }
    if (app.product.id === 23091) {
      app.views = app.views.concat(['on_model_mens', 'on_model_womens']);
    }

    app.analytics.init();
    // Hex value used for the global background color to apply to imagery
    app.backgroundColor = '#ffffff';
    app.registerHook.init();
    app.relatedProducts.init();
    app.model.init();
    app.cart.init();
    app.menu.init();
    app.additionalInformation.init();
    app.price.init();
    // app.startover.init();
    app.jzoom.init();
    // app.share.init();
    app.monogram.init();
    app.updateRecipeOms.init();
    // set globally the default width dialog.
    app.appUtils.setDialogWidth();
    app.appUtils.addCustomizeMobileHandlers();

    // app.collapsable_areas.init();
    if (app.mergedParams.showLocalization) {
      app.localizationDropDown.init();
    }
    const colorAliases = ['select_color', 'color'];
    configure.on('recipe:change', function (changes) {
      if (app.mergedParams.showChanges) {
        console.log('changes', changes);
      }
      // we need to update the product data on every change.
      app.product = configure.run('getProduct');
      app.registerHook.updateSkuAndStyleElements(changes);
      if (app.appUtils.isDTGProduct()) {
        app.menu.updateMaxLength();
        changes.map(change => {
          if (colorAliases.includes(change.ca.alias)) {
            configure.run('registerHook', 'component.attributeSelector.swatch', app.registerHook.swatchesHooks);
          }
        });
      }
      app.price.init();

      // Select the "none" value for all AVs if customize_selection is No
      const customizeSelection = _.find(changes, { ca: { alias: 'customize_selection' } });
      if (customizeSelection && customizeSelection.av.vendorId === 'No') {
        app.getRenderableAttributes(false, attr => {
          const noneValue = _.find(attr.values, { vendorId: 'none' });
          if (noneValue) {
            configure.run('selectValue', { ca: attr.id, av: noneValue.id });
          }
        });
      }

      const leftChest = _.find(changes, { ca: { alias: 'left_chest' } });
      const centerChestMonogram = _.find(changes, { ca: { alias: 'center_chest_monogram' } });
      if (app.helpers.hasAttribute('upcharge_initials') && (leftChest || centerChestMonogram)) {
        // upcharge if text has been entered
        let hasUpcharge = centerChestMonogram && centerChestMonogram.av && centerChestMonogram.av.text;
        if (leftChest && leftChest.av) {
          if (leftChest.av.vendorId === 'center_pony') {
            // upcharge if center_pony and text is present
            const centerText = configure.run('getAttribute', { alias: 'center_chest_monogram' });
            hasUpcharge = centerText && centerText.value && centerText.value.text;
          } else {
            // no upcharge if not center_pony
            hasUpcharge = false;
          }
        }
        configure.run('selectValue', {
          ca: 'upcharge_initials',
          av: { vendorId: hasUpcharge ? 'Yes' : 'No' }
        });
      }

      // to avoid the update of oms ca prevent the propagation of relevant changes.
      // We don't pass to analytics the oms_ changes.
      changes = _.filter(changes, function (o) {
        return o.ca.alias.indexOf('oms') === -1 && o.ca.alias.indexOf('ugc') === -1;
      });
      if (changes.length > 0) {
        // Clear the timer if it exists as a timeout object
        clearTimeout(timer);
        app.analytics.track('recipe-changed', changes);
      }
    });

    if (app.utils.isMobile() || app.utils.isVerticalTablet()) {
      app.zoom.initMobile();
    } else {
      app.thumbnails.init();
      app.zoom.init();
    }

    if (unavailable
      && !unavailable.media
      // Adding font_size check because the catalog strips p13n which throws
      // an error on recipe load
      && _.head(unavailable).ca.alias !== 'font_size'
    ) {
      // Remove values from the dialog that are unavailable due to product changes
      unavailable = bypassKnownUnavailable(unavailable);
      if (!_.size(unavailable)) { return false; }

      unavailable = checkForSize(unavailable);

      const unavailableDialog = configure.run('createDialog', {
        customClassName: 'fc-unavailable-dialog',
        innerHTML: showUnavailableSelections(unavailable),
        type: 'html',
        title: app.l10n.getLang('unavailable_title', 'Oh No!'),
        width: 700
      }, function (err) {
        if (err) { throw err; }
        // Set the focus on the Close button X
        $('.fc-unavailable-dialog')
          .find('.fc-dialog-header')
          .find('.fc-button-pair')[0].focus();
      });

      $('.fc-review-button').on('click', function () {
        unavailableDialog.trigger('dialog:closeRequest');
      });
    }

    app.fixedPreview.init();

    app.performance.mark('configure:ui:create:end');
  }

  function createCatalogUI(showAllProducts) {
    // Reset the configure container
    $container
      .addClass(function () {
        return 'fc-catalog' + (showAllProducts ? ' fc-catalog-all' : '');
      })
      .html(require(__dirname + '/../../templates/catalog/catalog.html'));

    app.catalog.init(showAllProducts);
    app.collapsable_areas.init();
  }

  return {
    createCatalogUI,
    createConfigureUI
  };
};

module.exports = CreateConfigure;
